// ── Delivery Request Logic ────────────────────────────────────────────────────
// Handles the 📦 "Request Delivery" modal for foremans.
// Saves requests to Supabase delivery_requests table.

// ── Tab switcher (Block / Scaffolding / Other) ────────────────────────────────
const DELIV_TABS = {
  block:    { tab: 'delivTabBlock',    panel: 'delivPanelBlock' },
  scaffold: { tab: 'delivTabScaffold', panel: 'delivPanelScaffold' },
  other:    { tab: 'delivTabOther',    panel: 'delivPanelOther' },
};
function switchDelivTab(tab) {
  Object.keys(DELIV_TABS).forEach(k => {
    document.getElementById(DELIV_TABS[k].tab)?.classList.toggle('active', k === tab);
    const p = document.getElementById(DELIV_TABS[k].panel);
    if (p) p.style.display = k === tab ? '' : 'none';
  });
}

// ── Auto-refresh timer ────────────────────────────────────────────────────────
let _delivRefreshTimer = null;

// ── Floating running-total pill ───────────────────────────────────────────────
// Sums every quantity typed across all tabs; hidden at zero. One delegated
// listener on the page wires it for built-in AND dynamically rendered rows.
let _delivPillWired = false;
function _delivUpdatePill() {
  const pill = document.getElementById('delivTotalPill');
  if (!pill) return;
  let total = 0;
  document.querySelectorAll('#deliveryPage .delivery-qty, #deliveryPage .delivery-extra-qty')
    .forEach(el => { total += Math.max(0, Number(el.value) || 0); });
  pill.classList.toggle('on', total > 0);
  if (total > 0) pill.innerHTML = total + ' <small>item' + (total !== 1 ? 's' : '') + ' — Send</small>';
}
function _delivWirePill() {
  if (_delivPillWired) return;
  _delivPillWired = true;
  const page = document.getElementById('deliveryPage');
  if (page) page.addEventListener('input', (e) => {
    if (e.target && (e.target.classList.contains('delivery-qty') || e.target.classList.contains('delivery-extra-qty'))) _delivUpdatePill();
  });
}

// ── Extra materials catalog (admin-managed list shown under "Other Materials") ──
function _delivEsc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
let _delivMaterials = null;   // cached delivery_materials rows (null = not loaded yet)

// If the catalog can't be fetched (first open while offline), the form still
// has to work — these mirror the seeded built-in rows in delivery_materials.
const DELIV_BUILTIN_FALLBACK = {
  '20cm':    [['standards_2h','Standards 2H','pallets'],['bondbeams','Bondbeams','pallets'],['halves','Halves','pallets'],['multiblock','Multiblock','pallets'],['block_lock','Block Lock Bundle','bundles'],['wall_mesh','Wall Mesh','rolls']],
  '15cm':    [['standards_2h','Standards 2H','pallets'],['bondbeams','Bondbeams','pallets'],['halves','Halves','pallets'],['block_lock','Block Lock Bundle','bundles'],['wall_mesh','Wall Mesh','rolls']],
  '25cm':    [['standards_2h','Standards 2H','pallets'],['bondbeams','Bondbeams','pallets'],['halves','Halves','pallets'],['block_lock','Block Lock Bundle','bundles'],['wall_mesh','Wall Mesh','rolls']],
  '30cm':    [['standards_2h','Standards 2H','pallets'],['bondbeams','Bondbeams','pallets'],['halves','Halves','pallets'],['block_lock','Block Lock Bundle','bundles'],['wall_mesh','Wall Mesh','rolls']],
  'special': [['squints','Squints','pallets'],['half_height','Half-Height Pallet','pallets'],['slabs','Slabs','pallets'],['dbl_bullnose_full','Dbl Bullnose Full','pallets'],['dbl_bullnose_half','Dbl Bullnose Halves','pallets'],['sgl_bullnose_full','Sgl Bullnose Full','pallets'],['sgl_bullnose_half','Sgl Bullnose Halves','pallets']],
  'materials': [['mortar_tek','Mortar Tek','pallets'],['blockfill','Blockfill','pallets']],
};

let _delivCatalogOk = false;   // true once a real fetch succeeded

async function _delivLoadMaterials() {
  // Refetch on every form open so admin edits (renames, new items, hidden
  // items) show up without an app restart; keep the last good copy offline.
  try {
    const { data, error } = await sbClient.from('delivery_materials')
      .select('id, name, unit, category, type_key, sort_order').eq('active', true)
      .order('sort_order').order('name');
    if (!error && Array.isArray(data)) { _delivMaterials = data; _delivCatalogOk = true; }
    else if (_delivMaterials === null) _delivMaterials = [];
  } catch (_) { if (_delivMaterials === null) _delivMaterials = []; }
  _delivRenderMaterials();
}

// The whole pick-list is catalog-driven now. Rows with a type_key are the
// structural items — their quantities write the classic jsonb keys
// (items['20cm'].standards_2h…), so history, Reports and Edit Delivery keep
// working no matter how the admin renames them. Rows without a type_key are
// admin extras and flow into items.extra_materials as before.
function _delivRenderMaterials() {
  const mats = _delivMaterials || [];
  const byCat = {};
  mats.forEach(m => { const c = m.category || 'other'; (byCat[c] = byCat[c] || []).push(m); });

  const extraRow = m => `<div class="dsc-row"><span>${_delivEsc(m.name)}${m.unit ? ` <span class="dsc-unit">(${_delivEsc(m.unit)})</span>` : ''}</span>
      <input class="delivery-extra-qty dsc-qty" type="number" min="0" placeholder="0"
        data-mat-name="${_delivEsc(m.name)}" data-mat-unit="${_delivEsc(m.unit || '')}"></div>`;
  const builtinRow = (skey, m) => `<div class="dsc-row"><span>${_delivEsc(m.name)}${m.unit ? ` <span class="dsc-unit">(${_delivEsc(m.unit)})</span>` : ''}</span>
      <input class="delivery-qty dsc-qty" type="number" min="0" placeholder="0"
        data-skey="${_delivEsc(skey)}" data-tkey="${_delivEsc(m.type_key)}"></div>`;

  const wrap = document.getElementById('delivExtraMaterials');
  if (wrap) {
    const others = byCat.other || [];
    wrap.innerHTML = others.length ? `<div class="dsc">
      <div class="dsc-head"><span class="ip-ic"><i data-lucide="package"></i></span>Available Materials</div>
      ${others.map(extraRow).join('')}
    </div>` : '';
  }

  ['15cm', '20cm', '25cm', '30cm', 'special', 'materials'].forEach(cat => {
    const c = document.getElementById('delivRows_' + cat);
    if (!c) return;
    let list = byCat[cat] || [];
    // Offline / fetch failed → render the fallback built-ins so the form is
    // never empty. (An admin hiding items is a SUCCESSFUL fetch — respected.)
    if (!_delivCatalogOk && !list.some(m => m.type_key)) {
      list = DELIV_BUILTIN_FALLBACK[cat].map(([k, n, u]) => ({ type_key: k, name: n, unit: u }))
        .concat(list.filter(m => !m.type_key));
    }
    c.innerHTML = list.map(m => m.type_key ? builtinRow(cat, m) : extraRow(m)).join('');
  });
}

// ── Scaffolding catalog (Scaffolding tab) ─────────────────────────────────────
// The rental scaffold piece types from the Rentals tab ('Rental Scaffold'
// category only — TM Equipment scaffold is excluded on purpose). Quantities
// use the same .delivery-extra-qty inputs as Other Materials, so they flow
// into items.extra_materials with zero changes to submit / admin / email.
let _delivScaffold = null;   // cached list (null = not loaded yet)

async function _delivLoadScaffold() {
  if (_delivScaffold === null) {
    try {
      const { data } = await sbClient.from('equipment')
        .select('name, category, sort_order')
        .eq('category', 'Rental Scaffold')
        .order('sort_order').order('name');
      const seen = new Set();
      _delivScaffold = (data || []).filter(it => {
        const k = (it.name || '').trim().toLowerCase();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    } catch (_) { _delivScaffold = []; }
  }
  _delivRenderScaffold();
}

function _delivRenderScaffold() {
  const wrap = document.getElementById('delivScaffoldMaterials');
  if (!wrap) return;
  const items = _delivScaffold || [];
  if (!items.length) {
    wrap.innerHTML = '<div style="color:var(--text-muted);font-size:12px;padding:8px 0">Scaffold list unavailable — describe what you need under Other Materials.</div>';
    return;
  }
  wrap.innerHTML = `<div class="dsc">
    <div class="dsc-head"><span class="ip-ic"><i data-lucide="layers"></i></span>Scaffolding</div>
    ${items.map(m => `<div class="dsc-row" data-name="${_delivEsc(m.name.toLowerCase())}"><span>${_delivEsc(m.name)} <span class="dsc-unit">(pcs)</span></span>
      <input class="delivery-extra-qty dsc-qty" type="number" min="0" placeholder="0"
        data-mat-name="${_delivEsc(m.name)}" data-mat-unit="pcs"></div>`).join('')}
  </div>`;
}

// Search filters by hiding rows (never re-renders), so quantities already
// typed on other rows are kept and still collected on submit.
function delivScaffoldFilter() {
  const q = (document.getElementById('delivScaffoldSearch')?.value || '').toLowerCase().trim();
  document.querySelectorAll('#delivScaffoldMaterials .dsc-row').forEach(row => {
    row.style.display = !q || (row.dataset.name || '').includes(q) ? '' : 'none';
  });
}

// ── Open / Close (now uses full inner page instead of overlay) ────────────────
function openDeliveryModal() {
  // Populate project dropdown and pre-select from active project
  const sel = document.getElementById('delivProjectSelect');
  if (sel) {
    const projects = window.userProjects || [];
    sel.innerHTML = '<option value="">Select project…</option>' +
      projects.map(p => `<option value="${p}">${p}</option>`).join('');
    const activeProject = (typeof state !== 'undefined' && state.project) ? state.project : '';
    if (activeProject && projects.includes(activeProject)) sel.value = activeProject;
  }

  // Set default "needed by" to tomorrow
  const neededByEl = document.getElementById('delivNeededBy');
  if (neededByEl && !neededByEl.value) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    neededByEl.value = tomorrow.toISOString().split('T')[0];
  }

  // Reset all qty inputs, textareas, error
  document.querySelectorAll('.delivery-qty').forEach(el => { el.value = ''; });
  const timeEl = document.getElementById('delivNeededByTime');
  if (timeEl) timeEl.value = '';
  const otherEl = document.getElementById('delivOtherDesc');
  if (otherEl) otherEl.value = '';
  const notesEl = document.getElementById('delivNotes');
  if (notesEl) notesEl.value = '';
  const errEl = document.getElementById('deliveryError');
  if (errEl) errEl.textContent = '';

  // Load + render the admin-managed extra materials list (Other Materials tab)
  _delivLoadMaterials();

  // Load + render the scaffold catalog (Scaffolding tab); re-render clears
  // any quantities left over from a previous request.
  const scafSearch = document.getElementById('delivScaffoldSearch');
  if (scafSearch) scafSearch.value = '';
  _delivLoadScaffold();

  // Running-total pill: wire once, reset for the fresh (empty) form
  _delivWirePill();
  _delivUpdatePill();

  // Always open on Block tab
  switchDelivTab('block');

  // Navigate to the delivery inner page
  showInnerPage('deliveryPage');
  loadForemanDeliveries();

  // Auto-refresh every 30 s so foreman sees status updates without refreshing manually
  if (_delivRefreshTimer) clearInterval(_delivRefreshTimer);
  _delivRefreshTimer = setInterval(loadForemanDeliveries, 30_000);
}

function closeDeliveryModal() {
  if (_delivRefreshTimer) { clearInterval(_delivRefreshTimer); _delivRefreshTimer = null; }
  goHome();
}

// ── Foreman: load their own delivery history ──────────────────────────────────
// Two sections, so the submit button doesn't get pushed off-screen:
//   • Active (requested / on_schedule / on_hold)  → shown expanded with status pills
//   • Recent (delivered / confirmed_received / cancelled) → collapsed by default,
//     opens via a <details> if the foreman wants to peek at history.
// "Confirm Received" was removed — admin marking Delivered in the admin panel is
// the source of truth; an extra foreman confirmation step added friction without
// adding info.
async function loadForemanDeliveries() {
  const wrap = document.getElementById('foremanDeliveryHistory');
  if (!wrap || !window.currentUser) return;

  const { data, error } = await sbClient
    .from('delivery_requests')
    .select('id, status, requested_at, needed_by, needed_by_time, projects(name), items')
    .eq('foreman_id', window.currentUser.id)
    .order('requested_at', { ascending: false })
    .limit(30);

  if (error || !data?.length) {
    wrap.innerHTML = '<div style="color:var(--text-muted);font-size:13px">No requests yet.</div>';
    return;
  }

  const statusColors = {
    requested:          '#f59e0b',
    on_schedule:        '#3b82f6',
    on_hold:            '#8b5cf6',
    delivered:          '#22c55e',
    confirmed_received: '#10b981',
    cancelled:          '#6b7280'
  };
  const statusLabels = {
    requested:          '📋 Requested',
    on_schedule:        '🚛 On Schedule',
    on_hold:            '⏸ On Hold',
    delivered:          '✅ Delivered',
    confirmed_received: '✅ Received',
    cancelled:          'Cancelled'
  };

  const isActive = (s) => s === 'requested' || s === 'on_schedule' || s === 'on_hold';
  const active = data.filter(r => isActive(r.status));
  const done   = data.filter(r => !isActive(r.status));

  const renderCard = (r, compact) => {
    const color = statusColors[r.status] || 'var(--text-muted)';
    const label = statusLabels[r.status] || r.status;
    const proj  = r.projects?.name || '—';
    const date  = r.needed_by
      ? `Needed: ${r.needed_by}${r.needed_by_time ? ' ' + r.needed_by_time : ''}`
      : new Date(r.requested_at).toLocaleDateString();
    const canDelete = r.status === 'requested';

    const padding = compact ? '8px 10px' : '10px 12px';
    const fs      = compact ? '12px'    : '13px';

    return `
      <div style="padding:${padding};border-radius:8px;border-left:3px solid ${color};background:${color}15;margin-bottom:6px">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
          <div style="min-width:0">
            <div style="font-size:${fs};font-weight:600;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${proj}</div>
            <div style="font-size:11px;color:var(--text-muted);margin-top:2px">${date}</div>
          </div>
          <span style="font-size:11px;padding:3px 9px;border-radius:10px;background:${color}25;color:${color};font-weight:600;white-space:nowrap;flex-shrink:0">${label}</span>
        </div>
        ${canDelete ? `
          <button onclick="deleteForemanDelivery('${r.id}')"
            style="margin-top:6px;width:100%;padding:5px;border-radius:6px;background:none;color:var(--text-muted);border:1px solid var(--border);font-size:11px;cursor:pointer">
            🗑 Cancel Request
          </button>` : ''}
      </div>`;
  };

  let html = '';

  if (active.length) {
    html += `<div style="font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:var(--text-muted);margin:4px 0 8px">⏳ Pending · ${active.length}</div>`;
    html += active.map(r => renderCard(r, false)).join('');
  } else {
    html += `<div style="padding:14px;text-align:center;font-size:12px;color:var(--text-muted);font-style:italic;background:rgba(255,255,255,0.03);border-radius:8px;margin-bottom:8px">No pending deliveries — all caught up.</div>`;
  }

  if (done.length) {
    html += `
      <details style="margin-top:10px">
        <summary style="cursor:pointer;font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:var(--text-muted);padding:6px 0;list-style:none">
          ✅ Recent · ${done.length}
          <span style="font-size:11px;color:var(--text-muted);font-weight:400;text-transform:none;letter-spacing:0;margin-left:6px">(tap to expand)</span>
        </summary>
        <div style="margin-top:8px">${done.map(r => renderCard(r, true)).join('')}</div>
      </details>`;
  }

  wrap.innerHTML = html;
}

async function deleteForemanDelivery(id) {
  if (!confirm('Cancel this delivery request?')) return;
  const { error } = await sbClient.from('delivery_requests').delete().eq('id', id);
  if (error) { alert('Could not delete. Try again.'); return; }
  loadForemanDeliveries();
}

// ── Build items object from form inputs ───────────────────────────────────────
// The grids are catalog-rendered: each built-in input carries data-skey (the
// section: '15cm'…'30cm', 'special' or 'materials') and data-tkey (the STABLE
// jsonb key). Output shape is identical to the old hardcoded version — sizes
// as objects, mortar_tek/blockfill at the root — so nothing downstream changes.
function buildDeliveryItems() {
  const items = {
    '15cm': {}, '20cm': {}, special: {}, '25cm': {}, '30cm': {},
    mortar_tek: 0, blockfill: 0
  };

  document.querySelectorAll('#deliveryPage .delivery-qty[data-skey]').forEach(el => {
    const q = Math.max(0, parseInt(el.value || '0', 10) || 0);
    const skey = el.dataset.skey, tkey = el.dataset.tkey;
    if (!tkey) return;
    if (skey === 'materials') items[tkey] = q;
    else if (items[skey]) items[skey][tkey] = q;
  });

  // Admin-managed extra materials (any section) — denormalize name+unit
  // onto the request so it renders later without a join and survives catalog edits.
  const extra = [];
  document.querySelectorAll('.delivery-extra-qty').forEach(el => {
    const q = Math.max(0, parseInt(el.value || '0', 10) || 0);
    if (q > 0) extra.push({ name: el.dataset.matName || '', unit: el.dataset.matUnit || '', qty: q });
  });
  if (extra.length) items.extra_materials = extra;

  return items;
}

function totalItems(items) {
  let total = 0;
  ['15cm','20cm','25cm','30cm','special'].forEach(size => {
    if (items[size]) Object.values(items[size]).forEach(v => { total += v; });
  });
  total += (items.mortar_tek || 0) + (items.blockfill || 0);
  if (Array.isArray(items.extra_materials)) items.extra_materials.forEach(m => { total += Number(m.qty) || 0; });
  return total;
}

function getActiveDelivTab() {
  return document.getElementById('delivTabBlock')?.classList.contains('active') ? 'block' : 'other';
}

// ── Submit request ────────────────────────────────────────────────────────────
async function submitDeliveryRequest() {
  const btn      = document.getElementById('deliverySubmitBtn');
  const errEl    = document.getElementById('deliveryError');
  const projSel  = document.getElementById('delivProjectSelect');
  const projName = projSel?.value || '';
  const neededBy     = document.getElementById('delivNeededBy')?.value || null;
  const neededByTime = (document.getElementById('delivNeededByTime')?.value || '').trim() || null;
  const notes        = (document.getElementById('delivNotes')?.value || '').trim();

  if (errEl) errEl.textContent = '';

  if (!projName) {
    if (errEl) errEl.textContent = 'Please select a project.';
    return;
  }

  if (!neededBy) {
    if (errEl) errEl.textContent = '📅 Please pick the date you need this delivery by.';
    const dateEl = document.getElementById('delivNeededBy');
    dateEl?.focus();
    dateEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  // Merge BOTH tabs into one request. A foreman often fills out block
  // quantities AND types something under "Other Materials" — previously only
  // the active tab was saved and the other tab was silently dropped (block
  // order lost when they submitted from the Other Materials tab). Now block
  // quantities + any free-text note always travel together on the same request.
  const items     = buildDeliveryItems();
  const blockTotal = totalItems(items);
  const otherDesc  = (document.getElementById('delivOtherDesc')?.value || '').trim();
  if (otherDesc) items.other_materials = otherDesc;

  if (blockTotal === 0 && !otherDesc) {
    if (errEl) errEl.textContent = 'Add at least one quantity (blocks, scaffolding or materials), or describe what you need under Other Materials.';
    return;
  }

  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }

  try {
    const { data: project } = await sbClient
      .from('projects').select('id').eq('name', projName).single();

    if (!project) throw new Error('Project not found.');

    const { data: inserted, error } = await sbClient.from('delivery_requests').insert({
      project_id:     project.id,
      foreman_id:     window.currentUser.id,
      items,
      notes:          notes || null,
      needed_by:      neededBy || null,
      needed_by_time: neededByTime || null
    }).select('id').single();

    if (error) throw error;

    // Fire-and-forget email notification to admin (renan.j@trademarkmasonry.ca).
    // Failure here is non-fatal — the request is already saved; the admin will
    // see it in the panel either way. See Edge Function `notify-delivery`.
    if (inserted?.id) {
      sbClient.functions.invoke('notify-delivery', {
        body: { type: 'new', delivery_id: inserted.id }
      }).catch(e => console.warn('[delivery] admin email failed:', e));
    }

    closeDeliveryModal();

    if (typeof showToast === 'function') {
      showToast('📦 Delivery request sent!', 'success');
    }

    loadForemanDeliveries();

  } catch (err) {
    console.error('Delivery request failed:', err);
    if (errEl) errEl.textContent = err.message || 'Failed to send request. Try again.';
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Send Request'; }
  }
}
